import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import ConfirmationModal from '../../../common/modal/ConfirmationModal';
import SaveButton from '../../../common/button/SaveButton';
import {
  postParcelVoidPayment,
  postParcelVoidPaymentAuthorization,
  getParcelPinPadTransactions,
  registerParcelPinPadVoid,
} from '../../../../actions';
import { postTotalPosVoid } from '../../../../actions/totalpos/Totalpos';
import { numberFormatter } from '../../../../utils/number';
import printTotalPosVoid from '../../../../utils/printers/TotalPosVoid';

// flow: confirm -> two-step authorization (returns operationId and the pinpad
// sales of the parcel) -> void every pinpad sale on the terminal, if any ->
// void the payment with the authorized operationId
const VoidPaymentButton = ({
  parcelId,
  user: { id: userId },
  dispatchPostParcelVoidPayment,
  dispatchPostParcelVoidPaymentAuthorization,
  dispatchGetParcelPinPadTransactions,
  dispatchRegisterParcelPinPadVoid,
  dispatchPostTotalPosVoid,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPinPadModal, setShowPinPadModal] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [operationId, setOperationId] = useState(null);
  const [pinPadTransactionList, setPinPadTransactionList] = useState([]);
  const [pinPadProcessing, setPinPadProcessing] = useState({});
  // kept only so the cashier can (re)print the void voucher on demand - the
  // backend's pinpad transaction list doesn't carry the printing fields
  const [pinPadVoidResponses, setPinPadVoidResponses] = useState({});

  const allPinPadTransactionsVoided = pinPadTransactionList.every(
    (transaction) => transaction.voided,
  );

  const voidPayment = (authorizedOperationId) =>
    dispatchPostParcelVoidPayment({
      parcelId,
      operationId: authorizedOperationId,
    });

  const onAuthorized = (authorization) => {
    if (!authorization || !authorization.operationId) {
      toastr.error('Error', 'No se obtuvo la autorización de la operación');
      return;
    }

    const transactions = authorization.pinPadTransactionList || [];

    if (transactions.every((transaction) => transaction.voided)) {
      voidPayment(authorization.operationId);
      return;
    }

    setOperationId(authorization.operationId);
    setPinPadTransactionList(transactions);
    setPinPadProcessing({});
    setPinPadVoidResponses({});
    setShowPinPadModal(true);
  };

  const openConfirmationModal = () => setShowConfirmationModal(true);

  const closeConfirmationModal = () => setShowConfirmationModal(false);

  const handleConfirm = async () => {
    closeConfirmationModal();
    setAuthorizing(true);
    await dispatchPostParcelVoidPaymentAuthorization({
      parcelId,
      onAuthorized,
    });
    setAuthorizing(false);
  };

  const closePinPadModal = () => setShowPinPadModal(false);

  const refreshPinPadTransactionList = async () => {
    try {
      const refreshed = await dispatchGetParcelPinPadTransactions(parcelId);
      setPinPadTransactionList(refreshed || []);
    } catch ({ message }) {
      toastr.error('Error', message);
    }
  };

  // each sale is reversed on the pinpad and registered with the backend right
  // away, so a card is never left charged if the modal is closed mid-flow
  const handleVoidPinPadTransaction = async (transaction) => {
    setPinPadProcessing((prev) => ({ ...prev, [transaction.id]: true }));

    const response = await dispatchPostTotalPosVoid({
      financialReference: transaction.financialReference,
      operator: String(userId),
      amount: parseFloat(transaction.amount),
      qrPayment: transaction.paymentMethod === 'QR',
    });

    if (!response) {
      setPinPadProcessing((prev) => {
        const next = { ...prev };
        delete next[transaction.id];
        return next;
      });
      return;
    }

    setPinPadVoidResponses((prev) => ({ ...prev, [transaction.id]: response }));

    try {
      await dispatchRegisterParcelPinPadVoid({
        parcelId,
        originalTransactionId: transaction.id,
        operation: 'VOID',
        approved: Boolean(response.approved),
        responseCode: response.responseCode,
        response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    }

    await refreshPinPadTransactionList();
    setPinPadProcessing((prev) => {
      const next = { ...prev };
      delete next[transaction.id];
      return next;
    });
  };

  const handlePrintPinPadVoid = (transaction) => {
    const response = pinPadVoidResponses[transaction.id];
    if (!response) return;
    printTotalPosVoid(response, transaction.paymentMethod === 'QR');
  };

  // the backend only consumes the operationId when the void succeeds, so the
  // modal stays open on failure to retry with the same authorization
  const handleVoidPayment = async () => {
    if (!allPinPadTransactionsVoided) {
      return;
    }

    setVoiding(true);
    const voided = await voidPayment(operationId);
    setVoiding(false);

    if (voided) {
      closePinPadModal();
    } else {
      await refreshPinPadTransactionList();
    }
  };

  const pinPadProcessingInProgress =
    voiding || Object.keys(pinPadProcessing).length > 0;

  return (
    <>
      <Button
        color="danger"
        onClick={openConfirmationModal}
        disabled={authorizing}
      >
        <i className={`fa fa-${authorizing ? 'spinner fa-spin' : 'ban'}`} />{' '}
        Anular Pago
      </Button>
      <ConfirmationModal
        show={showConfirmationModal}
        onClickConfirm={handleConfirm}
        onClickCancel={closeConfirmationModal}
      />
      <Modal isOpen={showPinPadModal}>
        <ModalHeader>Anulación de Pago</ModalHeader>
        <ModalBody>
          <p>
            Se debe anular el cobro en el POS de cada tarjeta antes de confirmar
            la anulación del pago:
          </p>
          <ul className="list-unstyled">
            {pinPadTransactionList.map((transaction) => {
              const processing = Boolean(pinPadProcessing[transaction.id]);
              return (
                <li key={transaction.id} className="mb-2">
                  {`${transaction.cardApplication || ''} ${
                    transaction.cardNumber || ''
                  } - ${numberFormatter({
                    style: 'currency',
                    value: transaction.amount,
                  })}`}{' '}
                  {transaction.voided ? (
                    <>
                      <span className="text-success">
                        <i className="fa fa-check" /> Anulado
                      </span>{' '}
                      {pinPadVoidResponses[transaction.id] && (
                        <Button
                          size="sm"
                          color="secondary"
                          onClick={() => handlePrintPinPadVoid(transaction)}
                        >
                          <i className="fa fa-print" /> Imprimir
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button
                      size="sm"
                      color="danger"
                      disabled={processing}
                      onClick={() => handleVoidPinPadTransaction(transaction)}
                    >
                      <i
                        className={`fa fa-${
                          processing ? 'spinner fa-spin' : 'credit-card'
                        }`}
                      />{' '}
                      Anular en POS
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </ModalBody>
        <ModalFooter>
          <SaveButton
            action={handleVoidPayment}
            disabled={!allPinPadTransactionsVoided || voiding}
            text="Confirmar"
          />{' '}
          <Button
            color="secondary"
            onClick={closePinPadModal}
            disabled={pinPadProcessingInProgress}
          >
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

VoidPaymentButton.propTypes = {
  parcelId: PropTypes.number.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  dispatchPostParcelVoidPayment: PropTypes.func.isRequired,
  dispatchPostParcelVoidPaymentAuthorization: PropTypes.func.isRequired,
  dispatchGetParcelPinPadTransactions: PropTypes.func.isRequired,
  dispatchRegisterParcelPinPadVoid: PropTypes.func.isRequired,
  dispatchPostTotalPosVoid: PropTypes.func.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.get('user'),
});

const mapDispatchToProps = {
  dispatchPostParcelVoidPayment: postParcelVoidPayment,
  dispatchPostParcelVoidPaymentAuthorization:
    postParcelVoidPaymentAuthorization,
  dispatchGetParcelPinPadTransactions: getParcelPinPadTransactions,
  dispatchRegisterParcelPinPadVoid: registerParcelPinPadVoid,
  dispatchPostTotalPosVoid: postTotalPosVoid,
};

export default connect(mapStateToProps, mapDispatchToProps)(VoidPaymentButton);
